import { useCallback, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Search, Users, MoreVertical, Pencil, Trash2, Network, Store } from "lucide-react";
import { listEmployees, deleteEmployee } from "../../api/employees";
import { listBranches } from "../../api/branches";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Card, Button, Input, Select, EmptyState, TableSkeleton, ConfirmDialog, Avatar } from "../../components/ui";
import { StatusChip } from "../../components/Chips";
import Pagination from "../../components/Pagination";
import EmployeeFormModal from "./EmployeeFormModal";
import ShiftSummary from "../../components/WorkingDays";
import { formatDate } from "../../lib/records";
import { ActiveFilters } from "../../components/Filters";


export default function EmployeesList() {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [status, setStatus] = useState("");
  const [branchId, setBranchId] = useState(searchParams.get("partner") || "");
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null); // { top, left } in viewport coords
  const [formOpen, setFormOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const menuRef = useRef(null);

  // A chain gets a branch filter — fetch its outlets once to populate it.
  useEffect(() => {
    if (!isChain) return;
    listBranches({ limit: 100 })
      .then((res) => setBranches(res.data.items || []))
      .catch(() => setBranches([]));
  }, [isChain]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listEmployees({
        page,
        limit: 10,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        ...(isChain && branchId ? { partner: branchId } : {}),
      });
      setItems(res.data.items);
      setPagination(res.data.pagination);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, status, branchId, isChain]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [search, status, branchId]);

  // Keep the ?partner= query param (used when landing here from a branch's
  // "View staff" link) in sync with the selector.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (branchId) next.set("partner", branchId);
    else next.delete("partner");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  // Close dropdown when clicking outside the portal-rendered menu.
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on scroll/resize so a stale-positioned menu never gets left behind.
  useEffect(() => {
    if (!openMenuId) return;
    function close() {
      setOpenMenuId(null);
    }
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [openMenuId]);

  function toggleMenu(empId, e) {
    if (openMenuId === empId) {
      setOpenMenuId(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 140;
    setMenuAnchor({
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - menuWidth),
    });
    setOpenMenuId(empId);
  }

  function openCreate() {
    setEditingEmployee(null);
    setFormOpen(true);
  }
  function openEdit(emp) {
    setEditingEmployee(emp);
    setFormOpen(true);
    setOpenMenuId(null);
  }
  function onSaved() {
    setFormOpen(false);
    fetchData();
  }

  async function confirmDelete() {
    setDeleting(true);
    try {
      await deleteEmployee(deleteTarget._id);
      setDeleteTarget(null);
      fetchData();
    } finally {
      setDeleting(false);
    }
  }

  const activeMenuEmployee = items.find((emp) => emp._id === openMenuId) || null;

  // `partner` on an employee is the branch they belong to — it comes back
  // either populated or as a bare id. Absent means the chain's own head office.
  function branchNameOf(emp) {
    if (!emp.partner) return "Head office";
    if (typeof emp.partner === "object") return emp.partner.name || "Branch";
    return branches.find((b) => b._id === emp.partner)?.name || "Branch";
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8">
        <PageHeader
          title="Employees"
          subtitle={
            isChain
              ? "Staff across every branch, plus your own head-office team."
              : "Manage your organization's workforce, designations, and access levels."
          }
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Link to={isChain && branchId ? `/organization?partner=${branchId}` : "/organization"}>
                <Button variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 text-sm sm:text-base px-3 sm:px-4 py-1.5 sm:py-2">
                  <Network size={16} className="hidden sm:inline mr-1" /> View tree
                </Button>
              </Link>
              <Button
                onClick={openCreate}
                className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 text-sm sm:text-base px-3 sm:px-4 py-1.5 sm:py-2"
              >
                <Plus size={16} className="hidden sm:inline mr-1" /> Add employee
              </Button>
            </div>
          }
        />

        <Card className="bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50 overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-emerald-100/60 p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search by name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                />
              </div>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {["", "ACTIVE", "INACTIVE", "SUSPENDED"].map((s) => (
                  <button
                    key={s || "all"}
                    onClick={() => setStatus(s)}
                    className={`whitespace-nowrap rounded-full px-3 sm:px-3.5 py-1 sm:py-1.5 text-xs sm:text-body-sm font-medium transition-colors duration-200 ${
                      status === s
                        ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-md shadow-emerald-500/20"
                        : "bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700"
                    }`}
                  >
                    {s === "" ? "All" : s.charAt(0) + s.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
            </div>

            {isChain && (
              <div className="flex items-center gap-2">
                <Store size={14} className="shrink-0 text-gray-400" />
                <Select
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  className="w-full sm:w-64 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                >
                  <option value="">All branches + head office</option>
                  {branches.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            <ActiveFilters
              resultCount={loading ? null : pagination.total}
              filters={[
                { label: "Search", value: search, onClear: () => setSearch("") },
                {
                  label: "Status",
                  value: status ? status.charAt(0) + status.slice(1).toLowerCase() : "",
                  onClear: () => setStatus(""),
                },
                {
                  label: "Branch",
                  value: branches.find((b) => b._id === branchId)?.name,
                  onClear: () => setBranchId(""),
                },
              ]}
              onClearAll={() => {
                setSearch("");
                setStatus("");
                setBranchId("");
              }}
            />
          </div>

          {loading ? (
            <TableSkeleton rows={6} cols={5} />
          ) : items.length === 0 ? (
            <div className="p-8">
              <EmptyState
                icon={Users}
                title="No employees found"
                description={
                  search || status
                    ? "Try a different search or filter."
                    : isChain
                      ? "No staff records yet. Add someone to a branch or to your head office."
                      : "Add your first employee to start building your org."
                }
                action={
                  !search && !status && (
                    <Button
                      onClick={openCreate}
                      className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25"
                    >
                      <Plus size={16} /> Add employee
                    </Button>
                  )
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left">
                <thead>
                  <tr className="text-xs sm:text-label-sm uppercase tracking-wide text-gray-500 border-b border-emerald-100">
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Name</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Phone</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold hidden sm:table-cell">Designation</th>
                    {/* Shift drives attendance classification and payable
                        days, so whether someone has one is worth seeing on
                        the roster rather than only inside their record. */}
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold hidden lg:table-cell">Shift</th>
                    {isChain && (
                      <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold hidden md:table-cell">Branch</th>
                    )}
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Status</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold hidden md:table-cell">Reports to</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold hidden lg:table-cell">Last login</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 text-right w-12 sm:w-16">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-100/50">
                  {items.map((emp) => (
                    <tr key={emp._id} className="group text-xs sm:text-body-sm text-gray-800 hover:bg-emerald-50/50 transition-colors duration-200">
                      <td className="px-3 sm:px-4 py-2 sm:py-3">
                        <Link to={`/employees/${emp._id}`} className="flex items-center gap-2 sm:gap-3">
                          <Avatar name={emp.name} size={28} className="bg-gradient-to-br from-emerald-400 to-green-500 text-white text-xs" />
                          <div>
                            <p className="font-medium text-gray-800 text-xs sm:text-sm">{emp.name}</p>
                            <p className="text-xs text-gray-500">{emp.userId}</p>
                          </div>
                        </Link>
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-gray-600">+91 {emp.phone}</td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-gray-600 hidden sm:table-cell">{emp.designation || "—"}</td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 hidden lg:table-cell">
                        <ShiftSummary shift={emp.shift} />
                      </td>
                      {isChain && (
                        <td className="px-3 sm:px-4 py-2 sm:py-3 text-gray-600 hidden md:table-cell">
                          {branchNameOf(emp)}
                        </td>
                      )}
                      <td className="px-3 sm:px-4 py-2 sm:py-3">
                        <StatusChip status={emp.status} />
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-gray-600 hidden md:table-cell">
                        {emp.reportingTo?.length ? emp.reportingTo.map((m) => m.name).join(", ") : "Unassigned"}
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-gray-500 hidden lg:table-cell">
                        {emp.lastLoginAt ? formatDate(emp.lastLoginAt) : "Never"}
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-right relative">
                        <button
                          onClick={(e) => toggleMenu(emp._id, e)}
                          className="rounded-md p-1.5 text-gray-400 hover:bg-emerald-50 hover:text-emerald-700 transition-colors duration-200"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && items.length > 0 && (
            <div className="px-3 sm:px-4 border-t border-emerald-100/60 pt-3 sm:pt-4">
              <Pagination
                page={pagination.page}
                pages={pagination.pages}
                total={pagination.total}
                limit={pagination.limit}
                onPageChange={setPage}
              />
            </div>
          )}
        </Card>

        {/* Dropdown menu rendered via portal so it can never be clipped by
            the table's overflow-x-auto or the Card's overflow-hidden. */}
        {openMenuId && activeMenuEmployee && menuAnchor &&
          createPortal(
            <div
              ref={menuRef}
              style={{ position: "fixed", top: menuAnchor.top, left: menuAnchor.left, minWidth: 140 }}
              className="z-[9999] rounded-md border border-emerald-100 bg-white py-1 shadow-xl shadow-emerald-200/50"
            >
              <button
                onClick={() => openEdit(activeMenuEmployee)}
                className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-xs sm:text-body-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors duration-200"
              >
                <Pencil size={14} /> Edit
              </button>
              <button
                onClick={() => {
                  setDeleteTarget(activeMenuEmployee);
                  setOpenMenuId(null);
                }}
                className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-xs sm:text-body-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors duration-200"
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>,
            document.body
          )}

        {/* A chain filtering by a branch is almost always about to hire into
            that branch, so it seeds the form's branch selector. */}
        <EmployeeFormModal
          open={formOpen}
          onClose={() => setFormOpen(false)}
          employee={editingEmployee}
          onSaved={onSaved}
          defaultBranchId={isChain ? branchId : ""}
        />

        <ConfirmDialog
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
          loading={deleting}
          danger
          title="Delete employee?"
          description={`This will permanently delete ${deleteTarget?.name ?? "this employee"} from the system.`}
          confirmLabel="Delete"
        />
      </div>
    </div>
  );
}